import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Layout, Button, Input, Modal, Skeleton, Tabs, message, Tooltip, Tag, Select} from 'antd';

import { UploadOutlined } from '@ant-design/icons';
import { IoEllipsisVertical, IoPersonAdd, IoExit } from "react-icons/io5";
import { FaClock } from "react-icons/fa";

import UserContext from "../../../UserContext";
import { Link, useHistory } from 'react-router-dom';
import {
    getTeamInfo,
    createTeam,
    getCompetitionUser,
    getTeams,
    leaveTeam,
    getSubmissionDetails
} from '../../../actions/teams/utils';
import DefaultLayout from "../../../components/layouts/default";
import { CompetitionData, getLeaderboard, getMetaData, getPortalCompetition, registerCompetitionUser } from "../../../actions/competition";
import TeamAvatar from "../../../components/TeamAvatar";
import { createAvatar } from '@dicebear/core';
import { botttsNeutral } from '@dicebear/collection';
import CountdownTimer from "./CountDownTimer";
import LineChart from "./LineChart";

import { formatMseScore } from "./leaderboardColumns";
import LeaderBoardTab from "./Leaderboard";
import FindTeamsTab from "./FindTeams";
import NoCompetition from "./NoCompetition";

import path from 'path';
import './index.less';

const { Content } = Layout;

/**
 * Generates a unique avatar for each team member 
 * using a third party avatar library 
 * 
 * @param {string} username A team member's name
 *
 */
export const TeamMemberAvatar = ( {username}:{username: string}) => {
    const src = createAvatar(botttsNeutral, {
        seed: username,
        radius: 50,
        backgroundType: ["gradientLinear"]
    }).toDataUriSync();

    return <img className="teamMemberImage" src={src} alt="" />;
}

/**
 * Component that displays a team's data. Contains several components 
 * to create a team, view team members, upload submissions, view submission previews, 
 * and navigate to the matches page. 
 * 
 * @param {boolean} isLoadingTeamInfo    the current team the user is viewing
 * @param {any} compUser    schema that holds user's competition profile data
 * @param {any} rankData    schema that holds the team's ranking data
 * @param {any} teamInfo    schema that holds the team's general information
 * @param fetchTeamCallback callback function to update the team data and the competition user 
 * 
 */
const MyTeamTab = ( { isLoadingTeamInfo, compUser, rankData, teamInfo, metaData , fetchTeamsCallback}: 
                    { isLoadingTeamInfo: boolean, compUser: any, rankData: any, teamInfo: any, metaData: any, fetchTeamsCallback: () => void }
) => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    
    // Form field to create a new team with a name
    const [newTeamName, setNewTeamName] = useState<string>("");
    const [newTeamGroup, setNewTeamGroup] = useState<string>();
    const teamGroup = newTeamGroup ?? metaData?.teamGroups?.[0];

    // Modal states 
    const [isInviteModalVisible, setIsInviteModalVisible] = useState<boolean>(false);
    const [isLeaveModalVisible, setIsLeaveModalVisible] = useState<boolean>(false);
    
    const [latestSubmissionResult, setLatestSubmissionResult] = useState<any>(null);
    const [isLoadingLatestSubmissionResult, setIsLoadingLatestSubmissionResult] = useState<boolean>(false);

    const showLeaveModal = () => {
        setIsLeaveModalVisible(true);
    };

    const maxTeamSize = metaData?.maxTeamSize;
    const teamFull = typeof maxTeamSize === 'number' && compUser.competitionTeam?.teamMembers.length >= maxTeamSize;

    const showTeamLimitReached = () => {
        message.error(`Your team has reached max team size of ${maxTeamSize}!`)
    }

    const handleLeaveModalClose = () => {
        setIsLeaveModalVisible(false);
    };

    const showInviteModal = () => {
        setIsInviteModalVisible(true);
    };

    const handleInviteModalClose = () => {
        setIsInviteModalVisible(false);
    };

    /**
     * Converts a numeric rankinginto a ordinal label 
     * e.g. 1 = 1st, 2 = 2nd, 3 = 3rd, 4 = 4th...
     * 
     * @param number Represents the team's ranking
     * @returns {string} 
     */
    function getOrdinal(number: any) {
        const suffixes = ['th', 'st', 'nd', 'rd'];
        const v = number % 100;
        return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
    }

    /**
     * Helper function that removes the user from the team.
     * Invokes callback to update the dashboard data and competition user info
     */
    const handleLeaveTeam = () => {
        leaveTeam(compUser.competitionName, compUser.username, compUser.competitionTeam.teamName).then((res) => {
            message.success('Successfully left team.');
            handleLeaveModalClose();
            fetchTeamsCallback();
        })
        .catch(() => {});
    }

    /**
     * Helper function that calls API to create a new team
     * for the user upon submission of a new name
     * 
     */
    const handleClick = () => {

        if (newTeamName.length == 0) {
            message.info('Name cannot be empty');
            return;
        }

        if (metaData?.teamGroups && (!teamGroup || !metaData.teamGroups.includes(teamGroup))) {
            message.info('Division is not valid');
            return;
        }

        setIsLoading(true);

        createTeam(compUser.competitionName, compUser.username, newTeamName, metaData?.teamGroups ? teamGroup : undefined).then((res) => {
            message.success('Successfully made a new team!');
            fetchTeamsCallback();
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        if (!teamInfo?.submitHistory?.length || !metaData?.competitionName) {
            setLatestSubmissionResult(null);
            return;
        }

        let isCurrent = true;
        const latestSubmissionId = teamInfo.submitHistory[teamInfo.submitHistory.length - 1];

        setIsLoadingLatestSubmissionResult(true);
        getSubmissionDetails(metaData.competitionName, latestSubmissionId)
            .then((res) => {
                if (!isCurrent) return;
                setLatestSubmissionResult(res.data?.[0] || null);
            })
            .catch(() => {
                if (!isCurrent) return;
                setLatestSubmissionResult(null);
            })
            .finally(() => {
                if (!isCurrent) return;
                setIsLoadingLatestSubmissionResult(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [teamInfo?.submitHistory, metaData?.competitionName]);

    const latestSubmissionMessage =
        latestSubmissionResult?.message ||
        latestSubmissionResult?.error ||
        'No result message returned yet.';
    const latestSubmissionOk = latestSubmissionResult?.evaluationOk;
    const latestSubmissionCode = latestSubmissionResult?.evaluationCode;


    return (
        <>
        {isLoadingTeamInfo  ?
        <Skeleton active avatar = {true}  paragraph={{ rows: 6 }} />
        :
        <Content id="myTeamContainer" >
            
            {teamInfo !== null && teamInfo?.teamName && (
                <section>
                    <div id="teamMainContent">

                        <div id="teamHeader">
                            <div id="teamNameWrapper">
                                <div>
                                    <TeamAvatar teamName={teamInfo.teamName} size="large" />
                                </div>
                                <article>
                                    <h3>
                                        {teamInfo.teamName}
                                        {teamInfo.teamGroup && <Tag style={{marginLeft: "12px"}}>{teamInfo.teamGroup}</Tag>}
                                    </h3>
                                    <p id = "rankingTag">{rankData.rank ? `${getOrdinal(rankData.rank)} place` : "Unranked"}</p>
                                </article>
                            
                                <Button type = "link" size="large" id = "leaveTeamButton" onClick={showLeaveModal} icon = {<IoEllipsisVertical size = {28} style = {{color: 'var(--foreground)'}}/>}></Button>
                                <Modal
                                    centered
                                    title="Are you sure you want to leave this team?"
                                    okText="Yes"
                                    cancelText="No"
                                    open={isLeaveModalVisible}
                                    onOk={handleLeaveTeam}
                                    onCancel={handleLeaveModalClose}
                                >
                                </Modal>
                            </div>
                        </div>

                        <div id="teamScoreHistorySection">
                            <h3>Score History</h3>
                            {/* public_private comps keep scores in publicScoreHistory */}
                            <LineChart scoreHistory={(teamInfo != null) ? (teamInfo.publicScoreHistory?.length ? teamInfo.publicScoreHistory : teamInfo.scoreHistory).map(Number) : []}/>
                        </div>

                        <div id = "uploadFileSection">
                            <span id = "uploadFileHeader">
                                <h3>Upload Submission</h3>
                                <Tooltip title = {<p id = "submissionCountDown">{metaData?.submissionsEnabled ? <CountdownTimer endDate={metaData.endDate}/> : "Submissions have closed" }</p> }>
                                    <FaClock size = {28} />
                                </Tooltip>
                            </span>
                            
                            <Link to={{ pathname: `competitions/${compUser.competitionName}/upload`}} >
                                <Button size="large" className="uploadButton" icon = {<UploadOutlined size = {14}/>}>Upload</Button>
                            </Link>

                        </div>

                        <div id="latestSubmissionResultSection">
                            <h3>Latest Submission Result</h3>
                            {isLoadingLatestSubmissionResult ? (
                                <Skeleton active paragraph={{ rows: 2 }} />
                            ) : latestSubmissionResult ? (
                                <div id="latestSubmissionResultCard">
                                    <span id="latestSubmissionResultTags">
                                        {typeof latestSubmissionOk === 'boolean' && (
                                            <Tag color={latestSubmissionOk ? 'success' : 'error'}>
                                                {latestSubmissionOk ? 'Success' : 'Failed'}
                                            </Tag>
                                        )}
                                        {latestSubmissionCode && <Tag>{latestSubmissionCode}</Tag>}
                                    </span>
                                    <p>{latestSubmissionMessage}</p>
                                </div>
                            ) : (
                                <p id="latestSubmissionResultEmpty">
                                    Submit once to see your latest result here.
                                </p>
                            )}
                        </div>

                    </div>

                    <div id ="sideContent">

                        <div id = "membersBox">
                            <div id="teamMembersHeader">
                                <h3  className="heading">Members</h3>
                                
                                <div id="teamActionBtns">
                                <Button size="large" id="inviteButton" icon = {<IoExit size = {14}/>} onClick={()=>{
                                        Modal.confirm({
                                            title: 'Are you sure you want to leave the team?',
                                            content: 'You will need invite code to join again.',
                                            okText: 'Yes',
                                            okType: 'danger',
                                            cancelText: 'No',
                                            onOk: handleLeaveTeam,
                                        });
                                    }}>Leave</Button>
                                <Button size="large" id="inviteButton" onClick={teamFull ? showTeamLimitReached : showInviteModal} icon = {<IoPersonAdd size = {14}/>}>Invite</Button>

                                </div>
                                
                                <Modal 
                                    className = "inviteCodeModal"
                                    centered 
                                    cancelButtonProps={{ style: { display: 'none' } }} 
                                    title="Invite friends to your team" 
                                    open={isInviteModalVisible} 
                                    onOk={handleInviteModalClose}
                                    onCancel={handleInviteModalClose}
                                >
                                    <p>Share your Invite Code to your friend. Make sure to tell them your team name as well!</p>

                                    {typeof maxTeamSize === 'number' && (
                                        <p>Max team size: {maxTeamSize}</p>
                                    )}
                                    <h3 id = "inviteCode">
                                        {compUser.competitionTeam && compUser.competitionTeam.joinCode}
                                    </h3>
                                </Modal>
                            </div>

                            {compUser.competitionTeam && compUser.competitionTeam.teamMembers.map((member: string, index: number) => (
                                <div className="teamMember" key={index}>
                                    <TeamMemberAvatar username={member}></TeamMemberAvatar>
                                    <div className="teamMemberTextWrapper">
                                        <p className="teamMemberName">{member}</p>
                                    </div>
                                </div>
                            ))}

                        </div>

                        {/* <div id = "matchesBox">
                            <h3>Matches</h3>
                            <p>Check out your team's match replays to see how well youre performing!</p>
                            <Link to={`/matches/${teamInfo.teamName}`} rel="noopener noreferrer">
                                <p style = {{color: "white", marginTop: "1rem"}}>view all &gt;</p>
                            </Link>
                        </div> */}

                    </div>
                    
                </section>
            )}

            {teamInfo == null && (

                <section id="createTeamSection">
                    <Input
                        id="teamNameInput"
                        placeholder="New Team Name"
                        size="large"
                        onChange={(e) => setNewTeamName(e.target.value)}
                    >
                    </Input>
                    {metaData?.teamGroups && metaData.teamGroups.length > 0 && (
                        <>
                        <br />
                        <Select
                            placeholder="New Team Division"
                            style={{ width: '100%' }}
                            value={teamGroup}
                            onChange={(value) => {
                                setNewTeamGroup(value);
                            }}
                            options={metaData.teamGroups.map((g: string) => ({
                                label: g, value: g
                            }))}
                        />
                        </>
                    )}
                    <br />
                    <Button
                        loading={isLoading}
                        id="maketeambutton"
                        onClick={handleClick}
                        className="colorful1"
                    >
                        Create New Team
                    </Button>
                </section>
            )}
        </Content>
        }
    </>
    );
};


/**
 * Renders the entire dashboard for the competition portal.
 */
function CompetitionPortal({ competitionName }: { competitionName: string }) {
    const history = useHistory();

    // User profile data
    const { user } = useContext(UserContext);
    const [compUser, setCompUser] = useState<any>({});
    const [isRegistered, setIsRegistered] = useState<any>(false);

    // array for all teams
    const [allTeams, setAllTeams] = useState<Array<Object>>([]);

    // Competition Team Data
    const [teamInfo, setTeamInfo] = useState<any>({});
    const [isLoadingTeamInfo, setIsLoadingTeamInfo] = useState(true);

    const [isModalOpen, setIsModalOpen] = useState(false);

    // Tab state when navigating between different sections(Leaderboard, Find Teams, My Team)
    const [activeTab, setActiveTab] = useState('1'); 

    // Rank data for all teams
    const [rankingsData, setRankingsData] = useState<CompetitionData[]>([]);

    // Rank data for user's current team
    const [userRankData, setUserRankData] = useState<CompetitionData>({
        rank: 0,
        team: "",
        score: 0,
        submitHistory: [],
        scoreHistory: [],
        winHistory: [],
        lossHistory: [],
        drawHistory: [],
    }
    );

    // Loading states for leaderboard data fetching status
    const [isLoadingLeaderBoard, setIsLoadingLeaderBoard] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

    // meta data for current competition
    const [metaData, setMetaData] = useState<{
        competitionName: string;
        description: string;
        startDate: string;
        endDate: string;
        submissionsEnabled: boolean;
        leaderboardEnabled?: boolean;
        leaderboardType?: string;
        teamGroups?: string[];
        maxTeamSize?: number;
    } | null>(null);


    // Modal callback to show modal to register user for competition
    const showModal = () => {
        setIsModalOpen(true);
    };
    const handleCancel = () => {
        setIsModalOpen(false);
    };

    /** 
     * Fetches the comp user first to handle cases where the user
     * may have joined or left a team prior to this, so we need to get their most up to date
     * competition profile data.
     * More importantly, we need to check if the user is registered before we fetch 
     * any other data.
     **/
    const fetchTeams = () => {

        getCompetitionUser(competitionName, user.username).then((res) => {
            if (!res.data.registered) {
                message.info("you are not registered!");
                setTeamInfo(null);
                setIsLoadingTeamInfo(false);

                // Expose the register modal. When user registers, the page will reload
                showModal();
            }
            else {
                // update the competition user state
                setCompUser(res.data);

                // fetch all the teams
                getTeams(competitionName).then(res => {
                    if (res.data) {
                        const nonemptyteams = res.data.filter((team: any) => team.teamMembers.length > 0);
                        setAllTeams(Array.from(nonemptyteams))
                    
                    }
                })
                .catch(error => {
                    console.error(error);
                });
            }
        })
    }

    // Function to get competition meta data
    const getCompMetaData = async () => {
        getMetaData(competitionName).then((res) => {
            setMetaData(res.data);
        })
    }


    // Function to get ranking of user's team and all teams
    function updateRankings() {
        setIsLoadingLeaderBoard(true);
        getLeaderboard(competitionName).then((res) => {

            let newData = res.data
                // sort on backend for blockography
                // .sort((a: any, b: any) => {
                //     const latestScoreA = a.scoreHistory?.length > 0 ? a.scoreHistory[a.scoreHistory.length - 1] : -Infinity;
                //     const latestScoreB = b.scoreHistory?.length > 0 ? b.scoreHistory[b.scoreHistory.length - 1] : -Infinity;
                //     return latestScoreB - latestScoreA;
                // })
                // .sort((a: any, b: any) => b.bestScore - a.bestScore) // Sort by bestScore in descending order
                .map((d: any, index: number) => {
                        if (teamInfo != null) {
                            if (d.teamName === teamInfo.teamName) {
                                setUserRankData({
                                    rank: index + 1,
                                    team: d.teamName, 
                                    score: d.displayScore,
                                    submitHistory: d.submitHistory,
                                    scoreHistory: d.scoreHistory,
                                    publicScoreHistory: d.publicScoreHistory,
                                    privateScoreHistory: d.privateScoreHistory,
                                    winHistory: d.winHistory,
                                    lossHistory: d.lossHistory,
                                    drawHistory: d.drawHistory,
                                });
                            }
                        }
                        return {
                            rank: index + 1,
                            team: d.teamName, 
                            teamGroup: d.teamGroup,
                            score: d.displayScore,
                            submitHistory: d.submitHistory,
                            scoreHistory: d.scoreHistory,
                            publicScoreHistory: d.publicScoreHistory,
                            privateScoreHistory: d.privateScoreHistory,
                            winHistory: d.winHistory,
                            lossHistory: d.lossHistory,
                            drawHistory: d.drawHistory,
                        };
                });

            setLastRefresh(new Date());
            setRankingsData(newData);
            setIsLoadingLeaderBoard(false);
            if (teamInfo?.teamName) {
                setIsLoadingTeamInfo(false);
            }
        });
    };


    /**
     * On first page load, check if user is logged in.
     * Otherwise, fetch all the teams and competition metadata
     */
    useEffect(() => {
        if (!user.loggedIn) {
            message.info('You need to login to upload submissions and participate');
            history.replace(path.join(window.location.pathname, '../../../login'));
        }

        else {

            // Set the selected tab and persist it local storage
            const storedActiveTab = localStorage.getItem("activeTab");
            if (storedActiveTab) {
                setActiveTab(JSON.parse(storedActiveTab));
            }

            // Fetch teams will set the comp user state and grab all teams
            setIsLoadingTeamInfo(true);

            fetchTeams();
            getCompMetaData();
        }

    }, []);



    /**
     * Function to directly get the udpated team information without any 
     * use effect dependencies. Useful whenever the user uploads  
     * submissions
     */
    const updateTeamInformation = () => {
        getTeamInfo(competitionName, compUser.competitionTeam.teamName).then((res) => {
            setTeamInfo(res.data);
        })
    }

    /**
     * Whenever the user calls fetchTeams(), the compUser state
     * will sometimes change. This useEffect will capture 
     * those state transitions and check the comp user
     * details. Here we use the compUser's team property
     * to update the current teamInfo object
     * 
     */
    useEffect(() => {
        if (Object.keys(compUser).length === 0) return;

        // Team members wait for rankings, see updateRankings
        if (compUser.competitionTeam != null) {
            setIsLoadingTeamInfo(true);
            updateTeamInformation();
        }
        else {
            setTeamInfo(null);
            setIsLoadingTeamInfo(false);
        }
    }, [compUser])



    /** 
     * Gets the updated rankings on first page load and 
     * based on the state of the user's membership to a team
     */
    useEffect(() => {
        updateRankings();

    }, [teamInfo]);


    // When user registers for compeititons, allow access to portal
    const onSubmit = () => {
        registerCompetitionUser(competitionName, user.username).then((res) => {
            if (res.data.msg == "Success") {
                window.location.reload();
                setIsRegistered(true);
            }
            else {
                message.info(res.data.msg);
            }
        })
    }

    const isWld = metaData?.leaderboardType === 'wld';

    return (
        <DefaultLayout>

            {/** Register Modal */}
            <Modal
                className="registerUserModal"
                width={800}
                centered
                closeIcon={false}
                open={isModalOpen}
                maskClosable={false}
                onCancel={handleCancel}
                title={<h3 className="modal-title">Register</h3>}
                footer={null}
            >
                <p>
                    Looks like we don't have you registered for {competitionName} yet. Click register below to get started. The page will
                    reload once we confirm your registration. Otherwise, feel free to leave this page.
                </p>

                <div className="btn-container">
                    <Button onClick={onSubmit} className="colorful1">
                        Register
                    </Button>


                    <Button
                        onClick={() => {
                            history.push(path.join(history.location.pathname, '../competitions'));
                        }}
                        className="colorful2"
                    >Go Back</Button>

                </div>
            </Modal>

            {/** Main Content */}
            <Content className="CompetitionPortalPage">
                <Content id="portalHeader">
                    <section>
                        <div id="hello">
                            <h1 className="title2">Hello, <span className="colorful">{user.username}</span></h1>
                        </div>
                        <div id="portalBanner">
                            <p>Welcome to the AI Portal for {competitionName}</p>
                        </div>

                    </section>

                    {/** Header for displaying the user's team stats */}
                    <section id="portalStatsContent">

                        {isLoadingTeamInfo ? (
                            <Skeleton active  paragraph={{ rows: 4 }} ></Skeleton>
                        ) :
                            (<>
                                {compUser.competitionTeam == null ?
                                    <section id="noTeamMessage">
                                        <p>
                                            Uh oh! You're not in a team yet. Either make your own team or ask your friends to share their invite code,
                                            then navigate to Find Teams below to join their group!
                                        </p>
                                    </section>

                                    :
                                    <section id="portalStatsRow">
                                        {/* Titles Row */}
                                        <Row gutter={16} justify="center" className="stats-row titles">
                                        <Col span={6} className="stat-title">Submissions</Col>
                                        <Col span={6} className="stat-title">Latest Score</Col>
                                        <Col span={6} className="stat-title">Ranking</Col>
                                        {isWld && <Col span={6} className="stat-title">W-L-D</Col>}
                                        </Row>
                                        
                                        {/* Values Row */}
                                        <Row gutter={16} justify="center" className="stats-row values">
                                            <Col span={6} className="stat-col">
                                                <div className="stat-value">{userRankData.submitHistory.length}</div>
                                            </Col>

                                            <Col span={6} className="stat-col">
                                                <div className="stat-value"> 
                                                    {metaData?.leaderboardType === 'mse'
                                                        ? (userRankData.team ? formatMseScore(userRankData.score) : '—')
                                                        : userRankData.scoreHistory?.length > 0
                                                            ? userRankData.scoreHistory[userRankData.scoreHistory?.length - 1]
                                                            : 0
                                                    }
                                                </div>
                                            </Col>

                                            <Col span={6} className="stat-col">
                                                <div className="stat-value">{userRankData.rank || "—"}</div>
                                            </Col>

                                            {isWld && (
                                                <Col span={6} className="stat-col">
                                                    <div className="stat-value">
                                                        {userRankData.winHistory?.at(-1) ?? 0}-{userRankData.lossHistory?.at(-1) ?? 0}-{userRankData.drawHistory?.at(-1) ?? 0}
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </section>
                                }
                            </>
                            )}
                    </section>
                </Content>

                {/* Tab for switching between different sections */}

                <Content id="portalTabContent">
                    <Tabs
                        size="small"
                        animated={true}
                        tabPosition="top"
                        onTabClick={(key: string) => {
                            localStorage.setItem("activeTab", JSON.stringify(key));
                        }}
                        activeKey={activeTab} onChange={(key) => setActiveTab(key)}
                        items={
                            [
                                {
                                    label: <p>Leaderboard</p>,
                                    disabled: isLoadingTeamInfo,
                                    key: '1',
                                    children: <LeaderBoardTab
                                        rankData={rankingsData}
                                        lastRefresh={lastRefresh}
                                        updateRankingsCallback={updateRankings}
                                        isLoading={isLoadingLeaderBoard} 
                                        teamGroups={metaData?.teamGroups}
                                        leaderboardEnabled={metaData?.leaderboardEnabled}
                                        leaderboardType={metaData?.leaderboardType}
                                        metaLoaded={!!metaData}/>
                                },
                                {
                                    label: <p>Find Teams</p>,
                                    disabled: isLoadingTeamInfo,
                                    key: '2',
                                    children: <FindTeamsTab
                                        data={allTeams}
                                        user={user}
                                        compUser={compUser}
                                        registered={isRegistered}
                                        fetchTeamsCallback={fetchTeams}
                                        updateRankings={updateRankings} />
                                },
                                {
                                    label: <p>My Team</p>,
                                    disabled: isLoadingTeamInfo,
                                    key: '3',
                                    children: <MyTeamTab
                                        isLoadingTeamInfo = {isLoadingTeamInfo}
                                        compUser={compUser}
                                        rankData={userRankData}
                                        teamInfo={teamInfo}
                                        metaData={metaData}
                                        fetchTeamsCallback={fetchTeams} />,
                                },
                            ]
                        }
                    ></Tabs>
                </Content>
            </Content>

        </DefaultLayout>
    );
}

/**
 * Loads the competition admins put in the portal.
 */
function CompetitionPortalPage() {
    const [competitionName, setCompetitionName] = useState<string | null>();

    useEffect(() => {
        getPortalCompetition()
            .then((res) => setCompetitionName(res.data.competitionName))
            .catch(() => setCompetitionName(null));
    }, []);

    if (competitionName === undefined) {
        return <DefaultLayout><Skeleton active /></DefaultLayout>;
    }

    if (competitionName === null) {
        return <DefaultLayout><NoCompetition /></DefaultLayout>;
    }

    return <CompetitionPortal competitionName={competitionName} />;
}

export default CompetitionPortalPage;
